import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Coins, Trophy, Plus, Clock, ArrowsClockwise, X, Check, TrendUp, TrendDown, HourglassMedium, Target, ChartLine, ShieldCheckered } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CreateBetModal from '../components/CreateBetModal';
import TeenVestLoader from '../components/TeenVestLoader';

const TAB_DEFS = [
  { key: 'active',   label: 'Active',     icon: ChartLine },
  { key: 'pending',  label: 'Pending',    icon: HourglassMedium },
  { key: 'resolved', label: 'Resolved',   icon: ShieldCheckered },
  { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
];

const formatRelative = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  const diff = (dt.getTime() - Date.now()) / 1000;
  if (diff <= 0) return 'now';
  if (diff < 60) return `in ${Math.round(diff)}s`;
  if (diff < 3600) return `in ${Math.round(diff / 60)}m`;
  if (diff < 86400) return `in ${Math.round(diff / 3600)}h`;
  return `in ${Math.round(diff / 86400)}d`;
};

const formatDeadline = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const PredictionPill = ({ bet }) => {
  const Icon = bet.direction === 'above' ? TrendUp : TrendDown;
  const colorCls = bet.direction === 'above' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' : 'bg-pink-500/15 text-pink-300 border-pink-400/30';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-bold ${colorCls}`}>
      <Icon size={12} weight="fill" />
      {bet.direction.toUpperCase()}
    </span>
  );
};

const BetCard = ({ bet, currentUserId, onAccept, onDecline, onCancel }) => {
  const { format: fmt } = useCurrency();
  const navigate = useNavigate();
  const isCreator = bet.creator_id === currentUserId;
  const opponent = isCreator ? bet.opponent_username : bet.creator_username;
  const myPosition = isCreator ? bet.direction : (bet.direction === 'above' ? 'below' : 'above');
  const current = bet.current_price_usd;
  const target = bet.target_price_usd;

  // For active bets: am I winning if it resolved now?
  let inLead = null;
  if (bet.status === 'active' && current) {
    const creatorRight = bet.direction === 'above' ? current >= target : current < target;
    const iAmCreatorRight = isCreator ? creatorRight : !creatorRight;
    inLead = iAmCreatorRight;
  }

  const statusBadge = () => {
    if (bet.status === 'pending') return <span className="px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 text-[10px] uppercase tracking-wider font-bold">Pending</span>;
    if (bet.status === 'active')  return <span className="px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 text-[10px] uppercase tracking-wider font-bold">Live</span>;
    if (bet.status === 'resolved') {
      return bet.i_won
        ? <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] uppercase tracking-wider font-bold">You Won 🏆</span>
        : <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[10px] uppercase tracking-wider font-bold">You Lost</span>;
    }
    return null;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5"
      data-testid={`bet-card-${bet.id}`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/market/${bet.symbol}`)}
            className="text-2xl font-unbounded font-bold text-white hover:text-gold transition-colors"
            data-testid={`bet-symbol-${bet.id}`}
          >
            {bet.symbol}
          </button>
          <PredictionPill bet={bet} />
        </div>
        {statusBadge()}
      </div>

      <p className="text-sm text-zinc-300 mb-3 leading-relaxed">
        <span className="font-semibold text-white">@{bet.creator_username}</span>
        {' '}bets <span className="font-semibold text-white">{bet.symbol}</span> will hit{' '}
        <span className={`font-mono font-bold ${bet.direction === 'above' ? 'text-emerald-300' : 'text-pink-300'}`}>
          {bet.direction} {fmt(target)}
        </span>
        {' '}by <span className="text-zinc-200 font-semibold">{formatDeadline(bet.deadline)}</span>.{' '}
        <span className="text-zinc-500">vs @{bet.opponent_username}</span>
      </p>

      {/* Live tracker for active bets */}
      {bet.status === 'active' && current ? (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] uppercase text-zinc-500 mb-0.5">Current</p>
            <p className="font-mono font-bold text-white text-sm">{fmt(current)}</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] uppercase text-zinc-500 mb-0.5">Target</p>
            <p className="font-mono font-bold text-gold text-sm">{fmt(target)}</p>
          </div>
          <div className={`text-center p-2 rounded-lg border ${inLead ? 'bg-emerald-500/15 border-emerald-400/30' : 'bg-red-500/15 border-red-400/30'}`}>
            <p className="text-[10px] uppercase text-zinc-500 mb-0.5">If now</p>
            <p className={`font-mono font-bold text-sm ${inLead ? 'text-emerald-300' : 'text-red-300'}`}>
              {inLead ? 'You\'d win' : 'You\'d lose'}
            </p>
          </div>
        </div>
      ) : null}

      {bet.status === 'resolved' && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] uppercase text-zinc-500 mb-0.5">Final</p>
            <p className="font-mono font-bold text-white text-sm">{fmt(bet.final_price_usd)}</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] uppercase text-zinc-500 mb-0.5">Target</p>
            <p className="font-mono font-bold text-gold text-sm">{fmt(target)}</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-white/5 border border-white/10">
            <p className="text-[10px] uppercase text-zinc-500 mb-0.5">Winner</p>
            <p className="font-mono font-bold text-white text-sm">@{bet.winner_username}</p>
          </div>
        </div>
      )}

      {bet.status === 'active' && bet.seconds_to_deadline != null && (
        <p className="text-xs text-zinc-400 flex items-center gap-1.5 mb-2">
          <Clock size={12} weight="fill" /> Resolves {formatRelative(bet.deadline)}
        </p>
      )}

      {/* Actions */}
      {bet.status === 'pending' && bet.is_opponent && (
        <div className="flex gap-2 mt-3">
          <Button onClick={() => onAccept(bet.id)} className="flex-1 bg-gradient-to-r from-emerald-400 to-emerald-600 text-black font-bold rounded-full" data-testid={`accept-bet-${bet.id}`}>
            <Check size={16} weight="bold" className="mr-1.5" /> Accept
          </Button>
          <Button onClick={() => onDecline(bet.id)} variant="ghost" className="flex-1 border border-white/10" data-testid={`decline-bet-${bet.id}`}>
            <X size={16} className="mr-1.5" /> Decline
          </Button>
        </div>
      )}
      {bet.status === 'pending' && bet.is_creator && (
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-zinc-400">Waiting on @{bet.opponent_username} to accept...</span>
          <button onClick={() => onCancel(bet.id)} className="text-red-400 hover:text-red-300 font-semibold" data-testid={`cancel-bet-${bet.id}`}>
            Cancel
          </button>
        </div>
      )}
    </motion.div>
  );
};

const LeaderboardRow = ({ entry, isMe }) => (
  <div className={`flex items-center justify-between gap-2 p-3 rounded-xl border ${isMe ? 'bg-gold/10 border-gold/30' : 'bg-white/[0.03] border-white/10'}`} data-testid={`bet-leaderboard-row-${entry.username}`}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 text-black flex items-center justify-center font-bold text-xs">
        {(entry.username || '?').slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-white truncate">
          {entry.nickname || entry.username}
          {isMe && <span className="ml-1.5 text-[10px] text-gold uppercase">You</span>}
        </p>
        <p className="text-[10px] text-zinc-500">@{entry.username}</p>
      </div>
    </div>
    <div className="flex items-center gap-4">
      <div className="text-right hidden sm:block">
        <p className="text-[10px] uppercase text-zinc-500">Wins / Losses</p>
        <p className="font-mono text-sm text-white">{entry.wins} <span className="text-zinc-500">/</span> {entry.losses}</p>
      </div>
      <div className="text-right">
        <p className="text-[10px] uppercase text-zinc-500">Total</p>
        <p className="font-mono text-sm text-white">{entry.total}</p>
      </div>
      <div className="text-right min-w-[64px]">
        <p className="text-[10px] uppercase text-zinc-500">Accuracy</p>
        <p className="font-mono text-base font-bold text-emerald-300">{entry.accuracy_pct.toFixed(0)}%</p>
      </div>
    </div>
  </div>
);

const Bets = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [bets, setBets] = useState({ active: [], pending: [], resolved: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [bRes, lRes, sRes] = await Promise.all([
        api.get('/bets/mine'),
        api.get('/bets/leaderboard'),
        api.get('/bets/stats'),
      ]);
      setBets(bRes.data || { active: [], pending: [], resolved: [] });
      setLeaderboard(lRes.data?.leaderboard || []);
      setStats(sRes.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load bets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Light auto-refresh every 30s for the active tab (live price tracker)
  useEffect(() => {
    if (activeTab !== 'active') return;
    const id = setInterval(loadAll, 30000);
    return () => clearInterval(id);
  }, [activeTab, loadAll]);

  const handleAccept = async (id) => {
    try {
      await api.post(`/bets/${id}/accept`);
      toast.success('Bet accepted — game on 🎮');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not accept'); }
  };
  const handleDecline = async (id) => {
    try {
      await api.post(`/bets/${id}/decline`);
      toast.message('Bet declined');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not decline'); }
  };
  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this bet?')) return;
    try {
      await api.post(`/bets/${id}/cancel`);
      toast.message('Bet cancelled');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
  };
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };
  const handleCreated = () => {
    setShowCreate(false);
    setActiveTab('pending');
    loadAll();
  };

  const counts = {
    active: bets.active?.length || 0,
    pending: bets.pending?.length || 0,
    resolved: bets.resolved?.length || 0,
    leaderboard: leaderboard.length,
  };

  return (
    <div className="min-h-screen noise-bg" data-testid="bets-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Coins size={24} weight="fill" className="text-gold" />
              <h1 className="text-3xl md:text-4xl font-unbounded font-bold text-white">TeenVest Bets</h1>
            </div>
            <p className="text-zinc-400">Predict where stocks are headed. Beat your friends. Climb the forecaster leaderboard.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleManualRefresh} variant="ghost" disabled={refreshing} className="border border-white/10" data-testid="bets-refresh-button">
              <ArrowsClockwise size={16} className={refreshing ? 'animate-spin' : ''} />
            </Button>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full px-5 hover:scale-[1.02] transition-transform"
              data-testid="create-bet-button"
            >
              <Plus size={16} weight="bold" className="mr-1.5" /> Create Bet
            </Button>
          </div>
        </motion.div>

        {/* My stats */}
        {stats && stats.total > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="glass-card p-4 text-center" data-testid="my-stats-wins"><p className="text-[10px] uppercase text-zinc-500 mb-1">My Wins</p><p className="text-2xl font-unbounded font-bold text-emerald-300">{stats.wins}</p></div>
            <div className="glass-card p-4 text-center" data-testid="my-stats-losses"><p className="text-[10px] uppercase text-zinc-500 mb-1">Losses</p><p className="text-2xl font-unbounded font-bold text-red-300">{stats.losses}</p></div>
            <div className="glass-card p-4 text-center" data-testid="my-stats-total"><p className="text-[10px] uppercase text-zinc-500 mb-1">Total Bets</p><p className="text-2xl font-unbounded font-bold text-white">{stats.total}</p></div>
            <div className="glass-card p-4 text-center" data-testid="my-stats-accuracy"><p className="text-[10px] uppercase text-zinc-500 mb-1">Accuracy</p><p className="text-2xl font-unbounded font-bold text-gold">{stats.accuracy_pct.toFixed(0)}%</p></div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1">
          {TAB_DEFS.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full whitespace-nowrap transition-all ${
                  isActive ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold' : 'bg-white/5 text-zinc-300 hover:bg-white/10'
                }`}
                data-testid={`bets-tab-${t.key}`}
              >
                <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                {t.label}
                <span className={`text-xs ${isActive ? 'text-black/60' : 'text-zinc-500'}`}>{counts[t.key]}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <TeenVestLoader size={88} label="Loading bets..." />
          </div>
        ) : activeTab === 'leaderboard' ? (
          <div className="space-y-2">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">No resolved bets yet — be the first to claim a spot 🏆</div>
            ) : leaderboard.map((row) => (
              <LeaderboardRow key={row.user_id} entry={row} isMe={row.user_id === user?.id} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {(bets[activeTab] || []).length === 0 ? (
              <div className="text-center py-16 glass-card">
                <Target size={48} className="text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 mb-4">No {activeTab} bets yet.</p>
                {activeTab !== 'resolved' && (
                  <Button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full px-5" data-testid="empty-state-create-button">
                    <Plus size={16} weight="bold" className="mr-1.5" /> Create your first bet
                  </Button>
                )}
              </div>
            ) : (
              (bets[activeTab] || []).map((b) => (
                <BetCard
                  key={b.id}
                  bet={b}
                  currentUserId={user?.id}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  onCancel={handleCancel}
                />
              ))
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showCreate && (
          <CreateBetModal
            onClose={() => setShowCreate(false)}
            onCreated={handleCreated}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Bets;
